import React, { useEffect, useState } from 'react';
import {
    loadMatrixRows,
    MatrixRow,
} from '../../../components/MatrixGroupEditor';
import { FieldIssue } from '../../../components/wizard/WizardShell';
import { MaterialBundlePayload, MaterialBundlePreflight } from '../../../api/bundle';
import FieldGroupEditorModal, {
    FieldGroupDraftBody,
    FieldGroupEditorState,
    newFieldGroupEditor,
} from '../../../components/schema/FieldGroupEditorModal';
import { physSchemaClient } from '../../../api/schema';
import { useCan } from '../../../store';
import { IssueByPath } from './useBundleDraft';
import {
    PendingTag,
    PrefixIssues,
} from '../../../components/wizard/PendingDict';
import {
    draftGroupItem,
    editorFromDraftGroup,
    parseGroupFields,
    upsertBy,
} from '../../../utils/bundleNewDicts';
import { alertDialog } from '../../../utils/dialog';
import { SchemaGroupField } from '../../../api/schema';
import {
    coerceSchemaFieldValue,
    extValueToInput,
    SchemaFieldInput,
    useDictOptions,
} from '../../device/deviceSchemaForm';

// 向导第 ② 步: 理化参数 (可选整步跳过).
//
// 组清单来自 preflight —— 服务端已按 (一级类别 + 二级类别) 圈定了适用组, 前端不做筛选,
// 也不硬编码任何字段: 字段名/单位/枚举候选都是模板给的, 模板改了这里立刻跟着变.
//
// 没用详情页的 MatrixGroupEditor: 那个组件自带"编辑/保存"按钮并直接调写接口,
// 而向导里的值只进草稿、要等整包过审才落库。矩阵表格在这里重画一遍 (~40 行),
// 比给那个组件加一个"不保存"模式更简单, 也不会把详情页的保存路径搅浑。

interface Props {
    payload: MaterialBundlePayload;
    setPayload: (p: MaterialBundlePayload) => void;
    preflight: MaterialBundlePreflight | null;
    issueByPath: IssueByPath;
}

type PreflightPhysGroup = MaterialBundlePreflight['physGroups'][number];

type GroupVals = Record<string, unknown>;

/**
 * PhysField 理化字段的展示契约 (由模板给出, 前端不硬编码任何字段). 与设备字段组同一结构:
 * type=str/bool/array 按类型录入, 缺省数值 —— 2026-10-24 前这里只认 options, str/bool 字段
 * 一律渲染成数字框, FDM 耗材的类型/供应商/可溶性等在页面上填不进去.
 */
type PhysField = SchemaGroupField;

/** coerce 输入框字符串 → 后端期望的类型, 与设备向导同口径 (转不成数字的原样留给 dryRun 报错). */
const coerce = coerceSchemaFieldValue;

function StepPhys({
    payload,
    setPayload,
    preflight,
    issueByPath,
}: Props) {
    const canCreateSchema = useCan('phys_schema', 'create');
    const [editor, setEditor] = useState<FieldGroupEditorState | null>(null);
    // 编辑草稿组时的原业务键 (改了键要先撤掉旧的那条)
    const [editingKey, setEditingKey] = useState('');
    const pendingList = payload.newDicts?.physSchemas || [];
    const existing = preflight?.physGroups || [];
    // 本套件待新建的草稿组 (newDicts.physSchemas) 排最前 (刚建的不用往下翻找) + 已有组;
    // 草稿组点名了二级类别且不含本材料的不列
    // (对本材料不开放, 同 preflight 按 type2 圈定的口径)
    const type2 = payload.material.type2;
    const groups: PreflightPhysGroup[] = [
        ...pendingList
            .filter(
                (s) =>
                    s.isEnabled !== false &&
                    (!s.cat2Keys?.length || !type2 || s.cat2Keys.includes(type2)),
            )
            .map((s) => ({
                groupKey: s.groupKey,
                label: s.label,
                layout: s.layout || 'flat',
                rowSource: s.rowSource || '',
                rowLabel: s.rowLabel || '',
                fields: parseGroupFields(s.fields) as unknown as Record<string, unknown>[],
            }))
            .reverse(), // 最新建的在最上
        ...existing,
    ];
    const isPending = (groupKey: string) =>
        pendingList.some((s) => s.groupKey === groupKey);
    // 矩阵组的行维度来自字典 (如波长/加工模式), 按 rowSource 惰性加载并缓存
    const [matrixRows, setMatrixRows] = useState<Record<string, MatrixRow[]>>({});
    const dictOpts = useDictOptions(
        groups.flatMap((g) =>
            ((g.fields || []) as unknown as PhysField[]).map((f) => f.optionsSource),
        ),
    );

    useEffect(() => {
        const srcs = Array.from(
            new Set(
                groups
                    .filter((g) => g.layout === 'matrix' && g.rowSource)
                    .map((g) => g.rowSource as string),
            ),
        );
        srcs.forEach((src) => {
            if (matrixRows[src]) return;
            loadMatrixRows(src)
                .then((rows) => setMatrixRows((prev) => ({ ...prev, [src]: rows })))
                .catch(() => undefined);
        });
        // matrixRows 故意不进依赖: 它只做缓存, 进依赖会自己触发自己
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preflight, payload.newDicts]);

    const setGroup = (groupKey: string, vals: GroupVals) => {
        const next = { ...(payload.physValues || {}) };
        if (Object.keys(vals).length === 0) delete next[groupKey];
        else next[groupKey] = vals;
        setPayload({ ...payload, physValues: next });
    };

    const setFlatField = (groupKey: string, f: PhysField, raw: string) => {
        const cur = { ...((payload.physValues || {})[groupKey] || {}) };
        if (raw === '') delete cur[f.key];
        else cur[f.key] = coerce(f, raw);
        setGroup(groupKey, cur);
    };

    const setMatrixCell = (
        groupKey: string,
        rowCode: string,
        f: PhysField,
        raw: string,
    ) => {
        const cur = { ...((payload.physValues || {})[groupKey] || {}) };
        const row = { ...((cur[rowCode] as Record<string, unknown>) || {}) };
        if (raw === '') delete row[f.key];
        else row[f.key] = coerce(f, raw);
        if (Object.keys(row).length === 0) delete cur[rowCode];
        else cur[rowCode] = row;
        setGroup(groupKey, cur);
    };

    if (!payload.material.type1) {
        return (
            <div className="card p-4 text-sm text-slate-500">
                请先在第 1 步选择材料类别, 本步的理化字段组按类别自动匹配。
            </div>
        );
    }

    // 缺组就地补 (2026-09-20): 与设备向导同一个编辑器, 归属模板锁死在当前一级类别.
    // 2026-09-29 起不单独提审: 表单确认后记进套件 newDicts.physSchemas, 当场可填,
    // 随整包一个工单落库 (此前单独提审, 工单没过之前这个组在向导里用不了)。
    const onDraft = (body: FieldGroupDraftBody) => {
        if (existing.some((g) => g.groupKey === body.groupKey)) {
            alertDialog(`类别 ${payload.material.type1} 下已有字段组 ${body.groupKey}, 直接在下面填值即可。`);
            return;
        }
        const item = {
            ...draftGroupItem(body),
            ...(body.cat2Keys?.length ? { cat2Keys: body.cat2Keys } : {}),
        };
        const rest =
            editingKey && editingKey !== body.groupKey
                ? pendingList.filter((s) => s.groupKey !== editingKey)
                : pendingList;
        const physValues = { ...(payload.physValues || {}) };
        if (editingKey && editingKey !== body.groupKey) delete physValues[editingKey];
        setPayload({
            ...payload,
            physValues,
            newDicts: { physSchemas: upsertBy(rest, item, (x) => x.groupKey) },
        });
        setEditor(null);
    };

    const dropPending = (groupKey: string) => {
        const physValues = { ...(payload.physValues || {}) };
        delete physValues[groupKey];
        const left = pendingList.filter((s) => s.groupKey !== groupKey);
        setPayload({
            ...payload,
            physValues,
            newDicts: left.length ? { physSchemas: left } : undefined,
        });
    };

    const editPending = (groupKey: string) => {
        const s = pendingList.find((x) => x.groupKey === groupKey);
        if (!s) return;
        setEditingKey(groupKey);
        setEditor(editorFromDraftGroup(s, { cat1: payload.material.type1 }));
    };

    const pendingIssues = (
        <PrefixIssues issueByPath={issueByPath} prefixes={['newDicts.physSchemas']} />
    );

    const newGroupBar = (
        <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">
                缺一个组? 就地新建, 不用中断向导去材料模板页。
            </span>
            <button
                type="button"
                className="btn-ghost text-xs ml-auto"
                onClick={() => {
                    setEditingKey('');
                    setEditor(newFieldGroupEditor({ cat1: payload.material.type1 }));
                }}
            >
                + 新建字段组
            </button>
        </div>
    );

    const editorModal = editor && (
        <FieldGroupEditorModal
            editor={editor}
            setEditor={setEditor}
            onClose={() => setEditor(null)}
            onSaved={() => setEditor(null)}
            onDraft={onDraft}
            client={physSchemaClient}
            domainLabel="材料理化"
            categoryScope
            matrixLayout
            lockScope
            scopeHint={payload.material.type1}
        />
    );

    if (groups.length === 0) {
        return (
            <>
                <div className="card p-4 space-y-2">
                    <div className="text-sm text-slate-500">
                        类别 {payload.material.type1} 下没有配置理化字段组, 本步可直接跳过。
                    </div>
                    {canCreateSchema && newGroupBar}
                    {pendingIssues}
                </div>
                {editorModal}
            </>
        );
    }

    return (
        <>
            <div className="infobox text-xs">
                理化参数可以全部留空, 创建后在材料详情页随时补录。这里只列出**本类别适用**的字段组。
            </div>
            {canCreateSchema && (
                <div className="card p-3 space-y-1">
                    {newGroupBar}
                    {pendingIssues}
                </div>
            )}
            {groups.map((g) => {
                const vals = (payload.physValues || {})[g.groupKey] || {};
                const filled = Object.keys(vals).length;
                const fields = (g.fields || []) as unknown as PhysField[];
                const rows = matrixRows[g.rowSource || ''] || [];
                return (
                    <div className="card p-4 space-y-3" key={g.groupKey}>
                        <div className="text-sm font-semibold flex items-center gap-2">
                            {g.label}
                            <span className="tag bg-slate-100 text-slate-500 font-normal font-mono">
                                {g.groupKey}
                            </span>
                            {isPending(g.groupKey) && (
                                <>
                                    <PendingTag />
                                    <button
                                        type="button"
                                        className="text-xs font-normal text-brand-600 hover:underline"
                                        onClick={() => editPending(g.groupKey)}
                                    >
                                        编辑定义
                                    </button>
                                    <button
                                        type="button"
                                        className="text-xs font-normal text-slate-400 hover:text-red-600"
                                        onClick={() => dropPending(g.groupKey)}
                                    >
                                        撤销新建
                                    </button>
                                </>
                            )}
                            <span className="ml-auto text-xs font-normal text-slate-400">
                                {filled > 0 ? `已填 ${filled} 项` : '未填'}
                            </span>
                        </div>
                        {g.layout === 'matrix' ? (
                            rows.length === 0 ? (
                                <div className="text-xs text-slate-400">
                                    行维度 ({g.rowSource}) 加载中或字典为空
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr>
                                                <th className="th">{g.rowLabel || '行'}</th>
                                                {fields.map((f) => (
                                                    <th className="th" key={f.key}>
                                                        {f.label}
                                                        {f.unit && (
                                                            <span className="text-slate-300">
                                                                {' '}
                                                                ({f.unit})
                                                            </span>
                                                        )}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((r) => {
                                                const rowVals =
                                                    (vals[r.code] as Record<string, unknown>) ||
                                                    {};
                                                return (
                                                    <tr key={r.code}>
                                                        <td className="td font-mono text-xs">
                                                            {r.label}
                                                        </td>
                                                        {fields.map((f) => (
                                                            <td className="td" key={f.key}>
                                                                <SchemaFieldInput
                                                                    field={f}
                                                                    value={extValueToInput(rowVals[f.key])}
                                                                    dictOpts={dictOpts}
                                                                    onChange={(v) =>
                                                                        setMatrixCell(
                                                                            g.groupKey,
                                                                            r.code,
                                                                            f,
                                                                            v,
                                                                        )
                                                                    }
                                                                />
                                                            </td>
                                                        ))}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )
                        ) : (
                            <div className="grid grid-cols-3 gap-3">
                                {fields.map((f) => (
                                    <div key={f.key}>
                                        <label className="field-label">
                                            {f.label}
                                            {f.unit && (
                                                <span className="text-slate-300"> ({f.unit})</span>
                                            )}
                                        </label>
                                        <SchemaFieldInput
                                            field={f}
                                            value={extValueToInput(vals[f.key])}
                                            dictOpts={dictOpts}
                                            onChange={(v) => setFlatField(g.groupKey, f, v)}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                        <FieldIssue issue={issueByPath[`physValues.${g.groupKey}`]} />
                    </div>
                );
            })}
            {editorModal}
        </>
    );
}

export default StepPhys;
